/**
 * The pricing FAQ, shared by the rendered page and its FAQPage schema.
 *
 * `/pricing` is a client component and its schema is emitted from the server
 * layout, so the Q&A pairs live here rather than in either file. That is the
 * point: schema describing an FAQ the page does not display is a policy
 * violation, and the only durable guard against that is one source of truth
 * both sides import. Edit a question here and the page and the structured
 * data change together.
 */

export interface PricingFaq {
  q: string;
  a: string;
}

export const PRICING_FAQS: Record<'en' | 'ar', PricingFaq[]> = {
  en: [
    {
      q: 'What counts as a resolved ticket?',
      a: "A ticket Open closes end-to-end without a human teammate stepping in, where the customer either confirms the outcome or doesn't re-open it. The exact definition is agreed in writing during onboarding.",
    },
    {
      q: 'Are there per-seat or per-message fees?',
      a: 'No. Teammates, messages, calls, and outbound sequences are unlimited. Your invoice is the per-resolution rate × the number of tickets Open resolved that month.',
    },
    {
      q: 'What happens when Open escalates to a human?',
      a: "If Open hands a ticket to one of your agents, you're not billed for it.",
    },
    {
      q: 'Is outbound really included?',
      a: 'Yes. Bulk imports, multi-channel sequences (WhatsApp, phone, SMS, email), AI personalization, response branching, and the API. You only pay for inbound tickets Open resolves.',
    },
    {
      q: 'What about telco, WhatsApp, and email send fees?',
      a: 'Pass-through carrier costs are billed at-cost from your provider with no markup. They typically come in under $0.10 per resolution.',
    },
    {
      q: 'Are there volume discounts?',
      a: 'Yes. Your rate sits between $0.75 and $0.90 per resolution based on monthly volume — higher volume, lower rate. The exact number is agreed in writing before you go live. Email pricing@open.cx for a quote.',
    },
    {
      q: 'How does the $2M refund guarantee fit in?',
      a: "If you're not 100% satisfied with Open in your first 120 days, every dollar of Open spend is refunded — up to $2 million.",
    },
    {
      q: 'Will I be charged tax?',
      a: 'Open is required to charge tax on certain products and services depending on your business location and tax status.',
    },
  ],
  ar: [
    {
      q: 'وش اللي يُحسب تذكرة محلولة؟',
      a: 'تذكرة يُغلقها Open من البداية إلى النهاية بلا تدخّل زميل بشري، ويكون العميل قد أكّد النتيجة أو ما أعاد فتحها. ونتفق على التعريف الدقيق كتابياً أثناء الإعداد.',
    },
    {
      q: 'فيه رسوم على كل موظف أو على كل رسالة؟',
      a: 'لا. أعضاء الفريق، والرسائل، والمكالمات، والتسلسلات الصادرة — كلها بلا حدود. وفاتورتك هي سعر الحل × عدد التذاكر اللي حلّها Open ذلك الشهر.',
    },
    {
      q: 'وش يحدث لما يصعّد Open التذكرة لوكيل بشري؟',
      a: 'إذا سلّم Open تذكرة لأحد وكلائك، فما تُحتسب عليك.',
    },
    {
      q: 'التسلسلات الصادرة مشمولة فعلاً؟',
      a: 'نعم. استيراد بالجملة، وتسلسلات متعددة القنوات (WhatsApp، هاتف، SMS، إيميل)، وتخصيص بالـ AI، وتفريع الردود، وواجهة API. وتدفع بس مقابل التذاكر الواردة اللي يحلها Open.',
    },
    {
      q: 'وش عن رسوم الاتصالات، وWhatsApp، والإيميل؟',
      a: 'نمرّر تكاليف المشغّل من مزوّدك بسعر التكلفة وبدون أي هامش. وعادةً تقل عن ٠,٤٠ ⃁ لكل حل.',
    },
    {
      q: 'فيه خصومات على الحجم؟',
      a: 'نعم. سعرك يقع بين ٢ و٣ ⃁ لكل حل حسب حجمك الشهري — حجم أكبر، سعر أقل. ونتفق على الرقم الدقيق كتابياً قبل ما تنطلق. راسل pricing@open.cx للحصول على عرض سعر.',
    },
    {
      q: 'وش علاقة ضمان استرجاع حتى ٧,٥ مليون ⃁ بكل هذا؟',
      a: 'إذا ما كنت راضي ١٠٠٪ عن Open خلال أول ١٢٠ يوم، نرجّع لك كل ريال أنفقته عليه — حتى ٧,٥ مليون ⃁.',
    },
    {
      q: 'هل تُحتسب عليّ ضريبة؟',
      a: 'Open ملزَم بتحصيل الضريبة على منتجات وخدمات معيّنة، حسب موقع نشاطك ووضعك الضريبي.',
    },
  ],
};
